import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import type { PersonYouKnow } from "@hub/shared";
import { Avatar, Button, SectionTitle, toast } from "@/ui";
import { api, useOpenContact, useSession } from "../_shims";
import s from "../network.module.css";

export const PEOPLE_YOU_KNOW_KEY = ["network", "people-you-know"] as const;

export function usePeopleYouKnow() {
  const { isSignedIn } = useSession();
  return useQuery({ queryKey: PEOPLE_YOU_KNOW_KEY, queryFn: () => api.peopleYouKnow(), enabled: isSignedIn, staleTime: 60_000 });
}

/**
 * People from your address book who are on the Hub and not yet connected with you: connect, or set them aside.
 * Renders nothing when there is no one, so Home and Connections can always include it.
 */
export function PeopleYouKnow({ limit, className }: { limit?: number; className?: string }) {
  const qc = useQueryClient();
  const open = useOpenContact();
  const list = usePeopleYouKnow();
  const refresh = () => {
    qc.invalidateQueries({ queryKey: PEOPLE_YOU_KNOW_KEY });
    qc.invalidateQueries({ queryKey: ["network", "connections"] });
  };
  const connect = useMutation({
    mutationFn: (p: PersonYouKnow) => api.requestConnection({ toUserId: p.userId, connectionType: "contact_match" }),
    onSuccess: (_, p) => {
      toast(`Connection request sent to ${p.contactName ?? p.displayName}.`, "success");
      refresh();
    },
    onError: (e) => toast(e instanceof Error ? e.message : "Could not send the request.", "danger"),
  });
  const dismiss = useMutation({
    mutationFn: (p: PersonYouKnow) => api.dismissPersonYouKnow(p.userId),
    onSuccess: refresh,
  });
  const people = list.data?.people ?? [];
  if (!people.length) return null;
  const shown = limit ? people.slice(0, limit) : people;
  return (
    <section className={className} data-testid="people-you-know">
      <SectionTitle>People you know on Hub</SectionTitle>
      <div className={s.sectionHint}>They are in your people and joined Hub. Connect to help each other with needs and offers. They are not told you have them saved.</div>
      <div className={s.stack}>
        {shown.map((p) => {
          const name = p.contactName ?? p.displayName;
          return (
            <div key={p.userId} className={s.connRow}>
              <Avatar name={name} src={p.avatarUrl} size={36} seed={p.userId} />
              <div style={{ minWidth: 0 }}>
                <div className={s.profileName}>
                  <button type="button" onClick={() => open(p.contactId)} style={{ background: "none", border: 0, padding: 0, font: "inherit", fontWeight: 600, cursor: "pointer", color: "inherit" }}>
                    {name}
                  </button>
                </div>
                <div className={`${s.small} ${s.muted}`}>{p.contactName && p.contactName !== p.displayName ? `On Hub as ${p.displayName}` : "On Hub"}</div>
              </div>
              <div className="u-row">
                <Button size="sm" variant="primary" icon={<UserPlus size={14} />} onClick={() => connect.mutate(p)} loading={connect.isPending && connect.variables?.userId === p.userId}>
                  Connect
                </Button>
                <Button size="sm" variant="ghost" onClick={() => dismiss.mutate(p)}>
                  Not now
                </Button>
              </div>
            </div>
          );
        })}
        {limit && people.length > limit && <div className={`${s.small} ${s.muted}`}>+{people.length - limit} more in Network, Connections</div>}
      </div>
    </section>
  );
}
